import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserPlus, Trash, PauseCircle, PlayCircle, Users, EnvelopeSimple, Phone, Percent, PencilSimple } from "@phosphor-icons/react";
import InviteLinkPanel from "@/components/InviteLinkPanel";

const emptyInvite = () => ({ first_name: "", last_name: "", email: "", phone: "" });

export default function TeamPage() {
  const { user } = useAuth();
  const isSubUser = !!user?.parent_user_id;
  const [members, setMembers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState(emptyInvite());
  const [saving, setSaving] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  // Iter54: mappa {sub_user_id: bool} per il toggle "Permetti modifica immobili".
  // Popolata da GET /team/margins (che ora ritorna anche can_edit_properties).
  const [permissions, setPermissions] = useState({});

  const load = useCallback(async () => {
    const [dirRes, marginsRes] = await Promise.all([
      api.get("/directory/operators?include_suspended=true"),
      // Per i sub-user, /team/margins ritorna 403 → ignoriamo l'errore silenziosamente.
      isSubUser ? Promise.resolve({ data: { members: [] } }) : api.get("/team/margins").catch(() => ({ data: { members: [] } })),
    ]);
    setMembers(dirRes.data || []);
    const perms = {};
    (marginsRes.data?.members || []).forEach(m => {
      perms[m.id] = !!m.can_edit_properties;
    });
    setPermissions(perms);
  }, [isSubUser]);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Nome, cognome e email obbligatori");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/team/invite", form);
      toast.success("Membro invitato");
      setTempPwd({ email: form.email, pwd: r.data.temp_password });
      setForm(emptyInvite());
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const suspend = async (u) => {
    await api.post(`/team/${u.id}/suspend`);
    toast.success(`${u.first_name} sospeso`);
    load();
  };
  const reactivate = async (u) => {
    await api.post(`/team/${u.id}/reactivate`);
    toast.success(`${u.first_name} riattivato`);
    load();
  };
  const del = async (u) => {
    await api.delete(`/team/${u.id}`);
    toast.success(`${u.first_name} eliminato`);
    load();
  };

  const togglePermission = async (memberId, next) => {
    // Optimistic update
    setPermissions(p => ({ ...p, [memberId]: next }));
    try {
      await api.patch(`/team/${memberId}/permissions`, { can_edit_properties: next });
      toast.success(next ? "Modifica immobili abilitata" : "Modifica immobili disabilitata");
    } catch (err) {
      setPermissions(p => ({ ...p, [memberId]: !next }));
      toast.error(err?.response?.data?.detail || "Errore aggiornamento permesso");
    }
  };

  const closeInvite = () => { setInviteOpen(false); setForm(emptyInvite()); setTempPwd(null); };

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Collaboratori</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Il mio Team</h1>
          <p className="text-muted-foreground mt-2">
            {isSubUser
              ? "Ecco il team a cui appartieni. Solo il Master può invitare, sospendere o gestire i permessi dei membri."
              : "Invita colleghi e collaboratori del tuo studio · condivideranno la tua fascia commerciale e plafond mensile."}
          </p>
        </div>
        {!isSubUser && (
          <Button className="ht-gradient border-0" onClick={() => setInviteOpen(true)} data-testid="team-invite-btn">
            <UserPlus size={16} className="mr-2" weight="bold" /> Invita membro
          </Button>
        )}
      </div>

      <Tabs defaultValue="members" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="members" data-testid="team-tab-members">
            <Users size={14} className="mr-1" /> Membri
          </TabsTrigger>
          {!isSubUser && (
            <TabsTrigger value="margins" data-testid="team-tab-margins">
              <Percent size={14} className="mr-1" /> Margini
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="members">
          {/* Iter209am — Sezione invito con link (pro master non sub-user) */}
          {!isSubUser && (
            <div className="mb-6">
              <InviteLinkPanel variant="team" testId="team-invite-panel" />
            </div>
          )}
          {members.length === 0 ? (
        <div className="kpi-card text-center py-16">
          <Users size={48} className="mx-auto mb-4 text-muted-foreground" weight="duotone" />
          <p className="text-muted-foreground">
            {isSubUser
              ? "Il tuo Master non ha ancora invitato altri collaboratori."
              : "Nessun membro nel team. Invita il primo collaboratore per iniziare."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map(m => (
            <div key={m.id} className={`kpi-card ${m.status === "suspended" ? "opacity-60" : ""}`} data-testid={`team-member-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-display text-lg font-semibold">
                    {m.first_name} {m.last_name}
                    {m.is_master && m.role === "professional" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider bg-purple-100 text-purple-800 px-2 py-0.5 rounded font-semibold">Master</span>
                    )}
                  </h3>
                  <span className={`text-xs font-semibold uppercase tracking-wider ${m.status === "approved" ? "text-green-700" : m.status === "suspended" ? "text-amber-700" : "text-muted-foreground"}`}>
                    {m.status === "approved" ? "Attivo" : m.status === "suspended" ? "Sospeso" : m.status}
                  </span>
                </div>
                {/* Iter54: pulsanti gestione visibili solo al Master. I sub-user vedono
                    l'elenco ma non possono agire su di esso. */}
                {!isSubUser && !m.is_master && (
                  <div className="flex gap-1">
                    {m.status === "approved" ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" title="Sospendi" data-testid={`team-suspend-${m.id}`}>
                            <PauseCircle size={16} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Sospendere {m.first_name} {m.last_name}?</AlertDialogTitle>
                            <AlertDialogDescription>Il membro non potrà più accedere alla piattaforma finché non lo riattivi.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction onClick={() => suspend(m)}>Sospendi</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <Button size="sm" variant="ghost" title="Riattiva" onClick={() => reactivate(m)} data-testid={`team-reactivate-${m.id}`}>
                        <PlayCircle size={16} color="#166534" weight="fill" />
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" title="Elimina" data-testid={`team-delete-${m.id}`}>
                          <Trash size={14} color="#991B1B" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare {m.first_name} {m.last_name}?</AlertDialogTitle>
                          <AlertDialogDescription>Azione irreversibile. Il membro verrà rimosso definitivamente dal tuo team.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(m)}>Elimina definitivamente</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              <div className="space-y-1 text-sm text-muted-foreground">
                <div className="flex items-center gap-2"><EnvelopeSimple size={13} /> {m.email}</div>
                {m.phone && <div className="flex items-center gap-2"><Phone size={13} /> {m.phone}</div>}
              </div>
              {/* Iter54: toggle permesso modifica immobili — solo il Master può
                  attivarlo/disattivarlo, e solo sui sub-user (non su se stesso). */}
              {!isSubUser && !m.is_master && (
                <div className="mt-3 pt-3 border-t flex items-center justify-between gap-3" data-testid={`team-perm-row-${m.id}`}>
                  <div className="flex items-center gap-2 text-sm">
                    <PencilSimple size={14} className="text-purple-700" weight="duotone" />
                    <span>Permetti modifica immobili</span>
                  </div>
                  <Switch
                    checked={!!permissions[m.id]}
                    onCheckedChange={(v) => togglePermission(m.id, v)}
                    disabled={m.status !== "approved"}
                    data-testid={`team-perm-toggle-${m.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
        </TabsContent>

        {!isSubUser && (
          <TabsContent value="margins">
            <MarginsPanel />
          </TabsContent>
        )}
      </Tabs>

      {/* Iter208.56 — Crediti generati dal team (spostati da Impostazioni) */}
      {!isSubUser && <TeamCreditsSection />}

      <Dialog open={inviteOpen} onOpenChange={(v) => { if (!v) closeInvite(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invita un membro al team</DialogTitle>
            <DialogDescription>Riceverà una password provvisoria per accedere. Sarà auto-approvato ereditando fascia e plafond.</DialogDescription>
          </DialogHeader>
          {tempPwd ? (
            <div className="space-y-3 py-2">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <div className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-2">Membro creato con successo</div>
                <div className="text-sm">Email: <code className="font-mono">{tempPwd.email}</code></div>
                <div className="text-sm mt-1">Password provvisoria: <code className="font-mono bg-white px-2 py-0.5 rounded border">{tempPwd.pwd}</code></div>
                <div className="text-xs text-muted-foreground mt-3">⚠️ Copia questa password e inviala al collaboratore — non sarà più visibile.</div>
              </div>
              <Button onClick={closeInvite} className="w-full ht-gradient border-0" data-testid="team-invite-done">Chiudi</Button>
            </div>
          ) : (
            <form onSubmit={invite} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome *</Label>
                  <Input required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="team-invite-first-name" /></div>
                <div><Label>Cognome *</Label>
                  <Input required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="team-invite-last-name" /></div>
              </div>
              <div><Label>Email *</Label>
                <Input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="team-invite-email" /></div>
              <div><Label>Telefono</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="team-invite-phone" placeholder="+39 333 1234567" /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={closeInvite}>Annulla</Button>
                <Button type="submit" disabled={saving} className="ht-gradient border-0" data-testid="team-invite-submit">
                  {saving ? "Invio..." : "Invita membro"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/** Tab "Margini" del team: consente al PRO Master di attivare il margine
 *  fisso e impostare la % applicata a TUTTI i collaboratori in modo uniforme.
 *  Iter57: rimossi gli override individuali — un unico valore per tutto il team.
 *  Iter58: aggiunto flag "Abilita margini collaboratori" + preview prezzo. */
function MarginsPanel() {
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(() => {
    setLoadError(null);
    return api.get("/team/margins")
      .then(r => setCfg(r.data))
      .catch(e => {
        const msg = e?.response?.status === 403
          ? "Solo il professionista titolare del team può gestire i margini."
          : (e?.response?.data?.detail || "Errore caricamento configurazione margini");
        setLoadError(msg);
      });
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchCfg = async (patch, successMsg) => {
    setSavingCfg(true);
    try {
      const r = await api.patch("/team/margins", patch);
      setCfg(c => ({ ...c, ...r.data }));
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally { setSavingCfg(false); }
  };

  const toggleFixed = (val) => patchCfg(
    { fixed_margin_active: val },
    val ? "Margine fisso attivato" : "Margine fisso disattivato",
  );

  const saveDefaultPct = (val) => {
    const pct = parseFloat(val);
    if (isNaN(pct) || pct < 0 || pct > 500) {
      toast.error("Percentuale non valida (0-500)");
      return;
    }
    return patchCfg({ default_sub_user_margin_pct: pct }, "Percentuale del team salvata");
  };

  const toggleSubMargin = (val) => patchCfg(
    { subuser_can_add_margin: val },
    val ? "Margini collaboratori abilitati" : "Margini collaboratori disabilitati",
  );

  const toggleBelowCost = (val) => patchCfg(
    { subuser_can_sell_below_cost: val },
    val ? "Vendite sottocosto abilitate" : "Vendite sottocosto disabilitate",
  );

  const saveMaxSubPct = (val) => {
    const pct = parseFloat(val);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Percentuale massima non valida (0-100)");
      return;
    }
    return patchCfg({ subuser_max_margin_pct: pct }, "Limite massimo aggiornato");
  };

  if (loadError) return (
    <div className="kpi-card p-6 border-amber-200 bg-amber-50/50 text-amber-900" data-testid="team-margins-error">
      {loadError}
    </div>
  );
  if (!cfg) return <div className="text-muted-foreground text-sm py-6">Caricamento configurazione margini…</div>;

  // Preview del prezzo che vedono i collaboratori su un servizio di riferimento (100€).
  const previewBase = 100;
  const previewFinal = cfg.fixed_margin_active
    ? Math.round(previewBase * (1 + (cfg.default_sub_user_margin_pct || 0) / 100) * 100) / 100
    : previewBase;
  const previewDelta = Math.round((previewFinal - previewBase) * 100) / 100;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Card 1 — Margine team fisso (Master → collaboratori) */}
      <div className="kpi-card p-6 space-y-5">
        <div>
          <h2 className="font-display text-xl font-semibold mb-1">Configurazione margine team</h2>
          <p className="text-sm text-muted-foreground">Se il margine fisso è attivo, i prezzi mostrati ai tuoi collaboratori nel marketplace verranno automaticamente ricaricati della percentuale definita qui sotto, in modo uniforme per tutto il team.</p>
        </div>
        <div className="flex items-center justify-between gap-4 py-2 border-y">
          <div>
            <Label htmlFor="fixed-margin-switch" className="text-base font-medium">Margine fisso</Label>
            <p className="text-xs text-muted-foreground mt-1">Attiva per applicare automaticamente il ricarico a tutti i collaboratori.</p>
          </div>
          <Switch
            id="fixed-margin-switch"
            checked={!!cfg.fixed_margin_active}
            onCheckedChange={toggleFixed}
            disabled={savingCfg}
            data-testid="team-margins-toggle"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label htmlFor="default-pct">Percentuale applicata (%)</Label>
            <p className="text-xs text-muted-foreground mb-2">Ricarico uguale per tutti i collaboratori del team.</p>
            <Input
              id="default-pct"
              type="number"
              step="0.5"
              min="0"
              max="500"
              defaultValue={cfg.default_sub_user_margin_pct}
              onBlur={(e) => {
                const v = parseFloat(e.target.value || 0);
                if (v !== cfg.default_sub_user_margin_pct) saveDefaultPct(v);
              }}
              data-testid="team-margins-default-pct"
            />
          </div>
        </div>
        {/* Preview prezzo: mostra al Master come cambia il prezzo di un servizio base */}
        <div
          className={`rounded-lg px-4 py-3 text-sm border ${cfg.fixed_margin_active ? "bg-purple-50 border-purple-200 text-purple-900" : "bg-slate-50 border-slate-200 text-slate-600"}`}
          data-testid="team-margins-preview"
        >
          <div className="text-[11px] uppercase tracking-wide font-semibold mb-1">Anteprima prezzo</div>
          {cfg.fixed_margin_active ? (
            <div>
              Servizio da <strong>{previewBase.toFixed(2)}€</strong> → i tuoi collaboratori vedranno{" "}
              <strong>{previewFinal.toFixed(2)}€</strong>
              {previewDelta > 0 && (
                <span className="text-purple-700"> (+{previewDelta.toFixed(2)}€ margine)</span>
              )}
            </div>
          ) : (
            <div className="italic">Margine fisso disattivo — i collaboratori vedranno il prezzo di listino senza ricarico.</div>
          )}
        </div>
      </div>

      {/* Card 2 — Margini applicati dai collaboratori sui clienti finali (Iter58) */}
      <div className="kpi-card p-6 space-y-5">
        <div>
          <h2 className="font-display text-xl font-semibold mb-1">Margini collaboratori</h2>
          <p className="text-sm text-muted-foreground">Controlla se i tuoi collaboratori possono aggiungere un margine proprio ai servizi rivenduti al cliente finale. Se disattivo, dovranno vendere al prezzo che tu hai impostato.</p>
        </div>
        <div className="flex items-center justify-between gap-4 py-2 border-y">
          <div>
            <Label htmlFor="sub-margin-switch" className="text-base font-medium">Abilita margini collaboratori</Label>
            <p className="text-xs text-muted-foreground mt-1">Se attivo, i collaboratori potranno impostare un margine al momento dell'acquisto entro il limite qui sotto.</p>
          </div>
          <Switch
            id="sub-margin-switch"
            checked={!!cfg.subuser_can_add_margin}
            onCheckedChange={toggleSubMargin}
            disabled={savingCfg}
            data-testid="team-submargin-toggle"
          />
        </div>
        {cfg.subuser_can_add_margin && (
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label htmlFor="max-sub-pct">Margine massimo consentito (%)</Label>
              <p className="text-xs text-muted-foreground mb-2">Tetto massimo che ogni collaboratore può aggiungere al prezzo (0–100%).</p>
              <Input
                id="max-sub-pct"
                type="number"
                step="0.5"
                min="0"
                max="100"
                defaultValue={cfg.subuser_max_margin_pct}
                onBlur={(e) => {
                  const v = parseFloat(e.target.value || 0);
                  if (v !== cfg.subuser_max_margin_pct) saveMaxSubPct(v);
                }}
                data-testid="team-submargin-max-pct"
              />
            </div>
          </div>
        )}
        {/* Iter59: sottocosto */}
        <div className="flex items-center justify-between gap-4 py-2 border-t">
          <div>
            <Label htmlFor="below-cost-switch" className="text-base font-medium">Permetti vendite sottocosto</Label>
            <p className="text-xs text-muted-foreground mt-1">Se attivo, i collaboratori possono vendere un servizio ad un prezzo inferiore al costo. La differenza verrà scalata dal tuo plafond. Se disattivo, il prezzo minimo è bloccato al costo del servizio.</p>
          </div>
          <Switch
            id="below-cost-switch"
            checked={!!cfg.subuser_can_sell_below_cost}
            onCheckedChange={toggleBelowCost}
            disabled={savingCfg}
            data-testid="team-belowcost-toggle"
          />
        </div>
      </div>
    </div>
  );
}

// ==================== TeamCreditsSection (Iter208.56) ====================
// Spostata da Settings.jsx (ex TeamMarginsPanel) → mostra il riepilogo dei
// crediti generati dai sotto-utenti sotto la pagina Team.
function TeamCreditsSection() {
  const [creditSummary, setCreditSummary] = useState(null);
  useEffect(() => {
    api.get("/team/credit-summary").then(r => setCreditSummary(r.data)).catch(() => {});
  }, []);
  if (!creditSummary) return null;
  return (
    <div className="mt-8 kpi-card" data-testid="team-credits-section">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-xl font-semibold">Crediti generati dal team</h3>
        <div className="text-2xl font-bold text-purple-700 font-mono" data-testid="team-total-credit">
          € {Number(creditSummary.total_credit || 0).toFixed(2)}
        </div>
      </div>
      {(creditSummary.items || []).length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Nessun collaboratore ha ancora generato crediti. Invita nuovi membri per iniziare.
        </p>
      ) : (
        <table className="w-full text-sm mobile-cards">
          <thead className="border-b text-xs uppercase text-muted-foreground">
            <tr className="text-left">
              <th className="py-2">Collaboratore</th>
              <th className="text-right">Pratiche</th>
              <th className="text-right">Margine mio</th>
              <th className="text-right">Margine suo</th>
              <th className="text-right pr-2">Totale credito</th>
            </tr>
          </thead>
          <tbody>
            {creditSummary.items.map(it => (
              <tr key={it.sub_user_id} className="border-b hover:bg-slate-50" data-testid={`credit-row-${it.sub_user_id}`}>
                <td data-label="Sotto-utente" className="py-3 font-medium">
                  {it.sub_user_name}
                  <div className="text-xs text-muted-foreground">{it.email}</div>
                </td>
                <td data-label="Pratiche" className="text-right font-mono">{it.practices_count}</td>
                <td data-label="Margine mio" className="text-right font-mono">€ {it.pro_default_total.toFixed(2)}</td>
                <td data-label="Margine suo" className="text-right font-mono">€ {it.sub_margin_total.toFixed(2)}</td>
                <td data-label="Totale credito" className="text-right font-mono pr-2 text-purple-700 font-bold">€ {it.total_credit.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

